
import React, { useState, useRef, useEffect } from 'react';
import { SocialVideo, Product } from '../types';
import { 
  Play, 
  Pause, 
  Heart, 
  Share2, 
  ShoppingCart, 
  Volume2, 
  VolumeX, 
  ArrowRight,
  Eye,
  Sparkles,
  Zap
} from 'lucide-react';
import { socialCommerceService } from '../services/socialCommerceService';
import { useCurrency } from '../contexts/CurrencyContext';

interface Props {
  video: SocialVideo;
  product: Product;
  onAddToCart: (p: Product) => void;
}

const VideoShoppingCard: React.FC<Props> = ({ video, product, onAddToCart }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { format } = useCurrency();

  const handlePlayToggle = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const handleLike = () => {
    if (!liked) {
      socialCommerceService.trackInteraction(video.id, 'like');
      setLiked(true);
    }
  };

  const handleBuy = () => {
    onAddToCart(product);
    socialCommerceService.trackInteraction(video.id, 'conversion');
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          videoRef.current?.play();
          setIsPlaying(true);
          socialCommerceService.trackView(video.id);
        } else {
          videoRef.current?.pause();
          setIsPlaying(false);
        }
      });
    }, { threshold: 0.7 });

    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [video.id]);

  return (
    <div className="relative w-full aspect-[9/16] max-w-sm mx-auto bg-black rounded-[40px] overflow-hidden shadow-2xl group ring-1 ring-white/10">
       <video 
         ref={videoRef}
         src={video.videoUrl}
         poster={video.thumbnailUrl}
         loop
         muted={isMuted}
         className="w-full h-full object-cover"
         onClick={handlePlayToggle}
       />

       {/* Overlay Gradient */}
       <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none"></div>

       {/* Top Status */}
       <div className="absolute top-6 left-6 flex items-center gap-2">
          <div className="bg-red-600 text-white px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
             <div className="w-1 h-1 bg-white rounded-full"></div> LIVE
          </div>
          <div className="bg-black/40 backdrop-blur-md text-white px-3 py-1 rounded-lg text-[8px] font-black flex items-center gap-1">
             <Eye className="h-3 w-3" /> {Math.floor(Math.random() * 500) + 50}
          </div>
       </div>

       {/* Right Sidebar Actions */}
       <div className="absolute right-4 bottom-32 flex flex-col gap-6 items-center">
          <button onClick={handleLike} className="flex flex-col items-center gap-1 group">
             <div className={`p-4 rounded-full backdrop-blur-xl transition-all ${liked ? 'bg-red-600 text-white scale-110 shadow-lg shadow-red-600/50' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                <Heart className={`h-6 w-6 ${liked ? 'fill-current' : ''}`} />
             </div>
             <span className="text-[10px] font-black text-white">{video.likes + (liked ? 1 : 0)}</span>
          </button>
          <button className="flex flex-col items-center gap-1">
             <div className="p-4 rounded-full bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 transition-all">
                <Share2 className="h-6 w-6" />
             </div>
             <span className="text-[10px] font-black text-white">{video.shares}</span>
          </button>
          <button onClick={() => setIsMuted(!isMuted)} className="p-4 rounded-full bg-white/10 backdrop-blur-xl text-white">
             {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
          </button>
       </div>

       {/* Bottom Product Info */}
       <div className="absolute bottom-6 left-6 right-6 space-y-4">
          <div className="space-y-1">
             <p className="text-white font-black text-lg leading-tight drop-shadow-lg">{video.caption}</p>
             <p className="text-white/60 text-xs font-bold flex items-center gap-1 uppercase tracking-widest">
                <Sparkles className="h-3 w-3 text-blue-400" /> AI Exclusive Content
             </p>
          </div>

          <div className="bg-white/10 backdrop-blur-2xl border border-white/20 p-4 rounded-[2rem] flex items-center gap-4 group/card hover:bg-white/20 transition-all">
             <div className="w-16 h-16 bg-white rounded-2xl overflow-hidden shrink-0">
                <img src={product.images[0]} className="w-full h-full object-cover" alt="" />
             </div>
             <div className="flex-grow min-w-0">
                <p className="text-white text-sm font-black truncate">{product.name}</p>
                <p className="text-blue-400 font-black">{format(product.price)}</p>
             </div>
             <button 
               onClick={handleBuy}
               className="bg-white text-gray-950 p-3 rounded-2xl hover:scale-110 active:scale-95 transition-all shadow-xl"
             >
                <ShoppingCart className="h-5 w-5" />
             </button>
          </div>
       </div>

       {/* Center Play Icon (Only when paused) */}
       {!isPlaying && (
         <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 animate-in zoom-in">
               <Play className="h-10 w-10 text-white fill-current" />
            </div>
         </div>
       )}
    </div>
  );
};

export default VideoShoppingCard;
